import type { IRawTiledLayer, IRawTiledMap, IRawTiledObject, IRect } from '@nova/types';

/** Default stub tile pitch in px. Matches the shared action-game `pxPerUnit`. */
export const STUB_MAP_TILE_PX = 150;

/** Default stub grid width in tiles. */
export const STUB_MAP_WIDTH_TILES = 32;

/** Default stub grid height in tiles. */
export const STUB_MAP_HEIGHT_TILES = 32;

export interface StubTiledMapOptions {
  widthTiles?: number;
  heightTiles?: number;
  tilePx?: number;
}

/**
 * Trims the stub `main-nav` rect after {@link buildStubTiledMap}.
 *
 * Stub shells are often sized to a **presentation** grid (playable interior plus a border ring).
 * `buildStubTiledMap` always places `main-nav` over the full shell in world pixels.
 * Pass `borderOutsetTiles * tilePx` so walkable nav covers only the playable interior;
 * presentation border tiles remain in the ground layer but stay off the nav mesh.
 *
 * No-op when `playableInteriorInsetPx` is zero.
 */
export function insetStubMainNavRect(map: IRawTiledMap, playableInteriorInsetPx: number): void {
  if (!(playableInteriorInsetPx > 0)) {
    return;
  }

  const navLayer = map.layers.find(
    (layer: IRawTiledLayer) => layer.name === 'nav' && layer.type === 'objectgroup'
  );
  if (!navLayer?.objects) {
    throw new Error('insetStubMainNavRect requires a nav objectgroup layer on the map.');
  }

  const mainNav = navLayer.objects.find((object) => object.name === 'main-nav');
  if (!mainNav || mainNav.width === undefined || mainNav.height === undefined) {
    throw new Error('insetStubMainNavRect requires a main-nav object on the nav layer.');
  }

  const shrunkWidth = mainNav.width - (2 * playableInteriorInsetPx);
  const shrunkHeight = mainNav.height - (2 * playableInteriorInsetPx);
  if (!(shrunkWidth > 0) || !(shrunkHeight > 0)) {
    throw new Error(`insetStubMainNavRect inset ${playableInteriorInsetPx}px exceeds main-nav size.`);
  }

  mainNav.x += playableInteriorInsetPx;
  mainNav.y += playableInteriorInsetPx;
  mainNav.width = shrunkWidth;
  mainNav.height = shrunkHeight;
}

/**
 * Builds minimal Tiled-shaped map JSON with open ground and one full-map nav rectangle.
 *
 * Satisfies {@link MapManager} mandatory `ground` + `nav` layers for navmesh and spawn setup.
 * Callers amend nav objects via {@link appendNavObjects}.
 */
export function buildStubTiledMap(options?: StubTiledMapOptions): IRawTiledMap {
  const width = options?.widthTiles ?? STUB_MAP_WIDTH_TILES;
  const height = options?.heightTiles ?? STUB_MAP_HEIGHT_TILES;
  const tileSize = options?.tilePx ?? STUB_MAP_TILE_PX;
  const widthPx = width * tileSize;
  const heightPx = height * tileSize;
  // Ground GIDs must be nonzero. AGE treats ground tile 0 as "empty space / off-map" and
  // rejects click-to-move onto it, so a zero-filled ground plane would be unwalkable.
  // The value is only a walkability marker here — with `tilesets` empty the client renders no
  // tile texture for it, so it is intentionally never resolved against a tileset firstgid.
  const groundData = new Array<number>(width * height).fill(1);

  return {
    compressionlevel: -1,
    width,
    height,
    infinite: false,
    nextlayerid: 3,
    nextobjectid: 2,
    orientation: 'orthogonal',
    renderorder: 'right-down',
    tiledversion: '1.11.0',
    tilewidth: tileSize,
    tileheight: tileSize,
    type: 'map',
    version: '1.10',
    tilesets: [],
    layers: [
      {
        id: 1,
        name: 'ground',
        type: 'tilelayer',
        x: 0,
        y: 0,
        width,
        height,
        opacity: 1,
        visible: true,
        data: groundData,
        objects: []
      },
      {
        id: 2,
        name: 'nav',
        type: 'objectgroup',
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        opacity: 1,
        visible: true,
        objects: [
          {
            id: 1,
            name: 'main-nav',
            type: '',
            class: '',
            x: 0,
            y: 0,
            width: widthPx,
            height: heightPx,
            rotation: 0,
            visible: true,
            properties: [{ name: 'main', type: 'bool', value: true }]
          }
        ]
      }
    ]
  };
}

/**
 * Appends nav-layer objects onto a stub/open map clone and renumbers object ids.
 * Accepts rect holes (`width`/`height`) and polygon holes (`polygon`).
 */
export function appendNavObjects(
  map: IRawTiledMap,
  objects: readonly IRawTiledObject[]
): IRawTiledMap {
  const next: IRawTiledMap = structuredClone(map);
  const navLayer = next.layers.find(
    (layer: IRawTiledLayer) => layer.name === 'nav' && layer.type === 'objectgroup'
  );
  if (!navLayer || !navLayer.objects) {
    throw new Error('appendNavObjects requires a nav objectgroup layer on the map.');
  }

  let nextId = Math.max(next.nextobjectid, 2);
  for (const object of objects) {
    navLayer.objects.push({
      ...object,
      id: nextId
    });
    nextId += 1;
  }
  next.nextobjectid = nextId;
  return next;
}

/** Named axis-aligned navmesh hole (world px). */
export interface NamedNavRectHole extends IRect {
  name: string;
}

/** Converts named world-px rects into raw Tiled nav hole objects. */
export function namedNavRectHolesToObjects(
  holes: readonly NamedNavRectHole[]
): IRawTiledObject[] {
  const objects: IRawTiledObject[] = [];
  for (const hole of holes) {
    if (!(hole.width > 0) || !(hole.height > 0)) {
      continue;
    }
    objects.push({
      id: 0,
      name: hole.name,
      type: '',
      class: '',
      x: hole.x,
      y: hole.y,
      width: hole.width,
      height: hole.height,
      rotation: 0,
      visible: true,
      properties: [{ name: 'navmeshHole', type: 'bool', value: true }]
    });
  }
  return objects;
}
